export default {
    chapters: {
        problem: "Problema",
        solution: "Soluzione",
        features: "Features",
        world: "Rotta AI",
        pricing: "Pricing",
        cta: "Attiva",
    },
    hero: {
        badge: "AI Workforce per la logistica",
        title: "Smetti di rincorrere email. Inizia a governare le spedizioni.",
        description: "EveMind unifica email, documenti, tracking e decisioni operative in un flusso unico guidato da agenti AI.",
        titleLine1: "Smetti di rincorrere email.",
        titleLine2: "Inizia a governare le spedizioni.",
        typewriterPrefix: "Inizia a",
        typewriterSuffixes: [
            "governare le spedizioni.",
            "riducendo i ritardi.",
            "proteggendo i margini.",
            "allineando ogni handoff.",
            "agendo prima del rischio.",
        ],
        subtitle: "EveMind unifica email, documenti, tracking e decisioni operative in un flusso unico guidato da agenti AI.",
        imageAlt: "Nave portacontainer in mare aperto — trasporto via mare",
        ctaPrimary: "Attiva account",
        ctaExplore: "Scopri le funzionalità",
        ctaLogin: "Accedi",
        ctaGoToApp: "Vai all'area riservata",
        ctaContact: "Contattaci",
    },
    problem: {
        label: "Il Problema",
        title: "Operazioni frammentate, decisioni lente, margini sotto pressione.",
        description: "Tra thread infiniti, allegati sparsi e aggiornamenti manuali, i team logistici reagiscono in ritardo invece di anticipare i rischi.",
        cards: {
            fragmented: {
                title: "Dati distribuiti su troppi canali",
                description: "Email, Excel, PDF e chat non comunicano: il contesto si perde a ogni handoff.",
            },
            late: {
                title: "Alert arrivano quando e gia tardi",
                description: "Le criticita emergono all'ultimo, quando i costi sono gia aumentati.",
            },
            manual: {
                title: "Troppo lavoro operativo manuale",
                description: "Il team consuma tempo su task ripetitivi invece che su decisioni strategiche.",
            },
        },
    },
    solution: {
        label: "Soluzione EveMind",
        title: "Una console AI che vede prima, decide più velocemente ed esegue meglio.",
        description: "EveMind costruisce una vista operativa unica su ogni spedizione e attiva automaticamente i prossimi step migliori.",
        bullets: {
            ingest: {
                title: "Ingestione automatica multi-sorgente",
                description: "Collega email, documenti e tracking in tempo reale.",
            },
            decide: {
                title: "Decision intelligence contestuale",
                description: "Prioritizza rischi e suggerisce azioni ad alto impatto.",
            },
            act: {
                title: "Execution assistita dagli agenti AI",
                description: "Risponde, aggiorna, verifica e coordina senza perdere il contesto.",
            },
        },
        timelineLabel: "Dal segnale all'azione",
        timeline: {
            capture: { time: "T+0 min", title: "Nuova email e documento acquisiti" },
            validate: { time: "T+2 min", title: "Cross-check automatico e anomaly detection" },
            predict: { time: "T+5 min", title: "Risk score aggiornato con spiegazione" },
            notify: { time: "T+6 min", title: "Alert e next action al team giusto" },
        },
    },
    agents: {
        care: {
            title: "Eve Care",
            subtitle: "Agente AI per il customer care",
            description: "Gestisce richieste cliente, allinea i team e mantiene storico conversazioni senza perdere contesto operativo.",
            features: [
                "Risponde alle richieste dei clienti in tempo reale",
                "Aggiorna automaticamente i clienti sullo stato delle spedizioni",
                "Registra automaticamente tutte le interazioni",
            ],
        },
        track: {
            title: "Eve Track",
            subtitle: "Agente per il tracciamento real-time",
            description: "Unifica tracking multi-vettore, aggiorna ETA e segnala deviazioni prima che impattino cliente e margini.",
            features: [
                "Collega dati satellitari e GPS in tempo reale",
                "Risponde automaticamente alle richieste di aggiornamento",
                "Genera notifiche per ritardi e anomalie",
            ],
        },
        quote: {
            title: "Eve Quote",
            subtitle: "Agente AI per quotazioni",
            description: "Legge richieste inbound, calcola prezzi con le tue regole e genera offerte pronte da inviare in pochi secondi.",
            features: [
                "Calcola tariffe in pochi secondi usando le tue regole di pricing",
                "Genera offerte professionali pronte da inviare",
                "Gestisce modifiche e aggiornamenti automaticamente",
            ],
        },
        read: {
            title: "Eve Read",
            subtitle: "Agente AI per la Lettura Documenti",
            description: "Estrae campi critici da documenti logistici, rileva mismatch e alimenta processi interni senza inserimenti manuali.",
            features: [
                "Legge documenti in qualsiasi formato automaticamente",
                "Estrae e struttura i dati chiave istantaneamente",
                "Invia i dati direttamente al tuo TMS",
            ],
        },
        plan: {
            title: "Eve Plan",
            subtitle: "Agente AI per la Pianificazione",
            description: "Propone piani di viaggio ottimali, ricalcola priorita live e riduce i colli di bottiglia operativi.",
            features: [
                "Suggerisce i piani di viaggio più efficienti quotidianamente",
                "Ricalcola le priorità in tempo reale quando cambiano le condizioni",
                "Considera disponibilità mezzi e restrizioni operative",
            ],
        },
    },
    features: {
        label: "Come risolviamo",
        title: "One AI agents fleet, one operational control layer",
        description: "Ogni agente copre una fase critica del flusso logistico: insieme riducono errori, tempi morti e costi di coordinamento.",
        showcase: {
            learnMore: "Pronto ad attivare EveMind?",
            care: {
                mockLabel: "Inbox in tempo reale",
                messages: [
                    "Cliente: ultimo ETA per MSCU7654321?",
                    "Eve Care: sequenza nave aggiornata — nuovo ETA 14 apr, 09:40 locale.",
                    "Cliente: quando potete inviare la bozza B/L rivista?",
                ],
            },
            track: {
                mockLabel: "Stream tracking",
                lines: ["Gate out Mombasa · stato OK", "Transito Colombo · deviazione −6h segnalata", "ATD aggiornato · feed armatoriale v2 applicato"],
            },
            quote: {
                mockLabel: "API quotazioni",
                responses: [
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "draft_ready" }',
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "sent", "margin": 0.124 }',
                ],
            },
            read: {
                mockLabel: "Pipeline estrazione",
                chips: ["B/L", "Invoice", "Packing list"],
                rows: [
                    { tone: "ok", title: "Invoice ↔ Packing list", meta: "Pesi allineati" },
                    { tone: "warn", title: "Shipper su B/L", meta: "Review umana in coda" },
                    { tone: "ok", title: "Fitosanitario", meta: "Registrato e archiviato" },
                ],
            },
            plan: {
                mockLabel: "Board priorità",
                rows: [
                    { rank: "P1", text: "Reefer pickup — terminal Genova, fascia 07:30" },
                    { rank: "P2", text: "Follow-up booking — MSC #448821" },
                    { rank: "P3", text: "Pacchetto doc — cliente ref ASH-2026-044" },
                ],
            },
        },
    },
    world: {
        label: "Storytelling 3D",
        title: "Una rotta intelligente che evolve ad ogni scroll.",
        description: "Scorri la rotta da Mombasa a San Francisco: ogni tappa coincide con uno stadio che EveMind copre su una tratta transoceanica reale.",
        stopPrefix: "Tappa {{index}}",
        stops: {
            care: {
                title: "Customer comms sempre allineate",
                description: "Il cliente riceve aggiornamenti coerenti e contestuali, senza rincorse manuali.",
                place: "Mombasa, Kenya — porto di imbarco",
            },
            track: {
                title: "Visibilita end-to-end sulla tratta",
                description: "Tracking multi-sorgente e alert proattivi su ritardi e deviazioni.",
                place: "Colombo, Sri Lanka — Oceano Indiano",
            },
            quote: {
                title: "Quotazioni rapide e consistenti",
                description: "Dalla richiesta all'offerta in tempi ridotti e con margini sotto controllo.",
                place: "Singapore — Stretto di Malacca",
            },
            read: {
                title: "Documenti verificati in automatico",
                description: "Cross-check e anomaly detection prima che il problema diventi costo.",
                place: "Shanghai, Cina — gateway del Pacifico",
            },
            plan: {
                title: "Pianificazione dinamica operativa",
                description: "Priorita e risorse si adattano in tempo reale a nuove condizioni.",
                place: "San Francisco, USA — arrivo e sbarco",
            },
        },
    },
    pricing: {
        label: "Pricing",
        title: "Un piano costruito sul tuo flusso operativo.",
        description: "Nessun listino standard uguale per tutti: definiamo insieme setup, volumi e obiettivi per massimizzare ROI e time-to-value.",
        cards: {
            volume: {
                title: "Basato sui volumi",
                description: "Scalabile su numero spedizioni, documenti e interazioni.",
            },
            operations: {
                title: "Basato sulla complessita",
                description: "Allineato a team, SOP, livelli di automazione e SLA.",
            },
            integration: {
                title: "Basato sulle integrazioni",
                description: "Connettori TMS/ERP e onboarding guidato per partire veloce.",
            },
        },
        ctaPrimary: "Parla con sales",
        ctaSecondary: "Attiva account",
    },
    finalCta: {
        title: "Pronto a trasformare la logistica in un vantaggio competitivo?",
        description: "Attiva EveMind e inizia a gestire rischi, documenti e tracking con una regia AI unica.",
        ctaPrimary: "Attiva account",
        ctaSecondary: "Prenota una demo",
    },
    footer: {
        legal: "Policy",
        contactTitle: "Pronto a trasformare la tua logistica?",
        contactDescription: "Contatta il team per saperne di più su EveMind.",
        contactButton: "Contattaci",
        loginLink: "Hai già un account?",
        loginButton: "Accedi",
    },
};
